import { useQuery } from "@tanstack/react-query"
import {
  getCustomerSuccessTickets,
  getCustomerSuccessTicketStats,
  getCustomerSuccessCenters,
} from "../api/customerSuccessTickets"
import {
  trackingStatusOptions,
  csatStatusOptions,
} from "../pages/CustomerSuccess/statusConfig"

export const useCustomerSuccessTicketsList = ({ page, limit, filters }) =>
  useQuery({
    queryKey: ["customer-success-tickets", "list", page, limit, filters],
    queryFn: () => getCustomerSuccessTickets({ page, limit, filters }),
    retry: false,
    // Keeps the current page on screen while the next one loads, instead of
    // collapsing to a spinner on every page/filter change.
    placeholderData: (previous) => previous,
  })

const TRACKING_STATUSES = trackingStatusOptions.map((o) => o.value)
const CSAT_STATUSES = csatStatusOptions.map((o) => o.value)

// Backs the overview strip's per-stage counts via the dedicated stats
// endpoint (one call, not one-per-status). `sharedFilters` deliberately
// excludes the table's own status filters — the stats endpoint always
// returns every status's count regardless of what the table is filtered to.
export const useCustomerSuccessOverviewCounts = (sharedFilters) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["customer-success-tickets", "stats", sharedFilters],
    queryFn: () => getCustomerSuccessTicketStats({ filters: sharedFilters }),
    retry: false,
    staleTime: 30_000,
  })

  const asMap = (statuses, counts) =>
    statuses.reduce((acc, status) => {
      acc[status] = { count: counts?.[status], isLoading, isError }
      return acc
    }, {})

  return {
    grandTotal: { count: data?.total, isLoading, isError },
    trackingCounts: asMap(TRACKING_STATUSES, data?.trackingStatusCounts),
    csatCounts: asMap(CSAT_STATUSES, data?.customerSatisfactionStatusCounts),
    centers: { ...data?.centers, isLoading, isError },
  }
}

export const useCustomerSuccessCenters = ({ page, limit, filters }) =>
  useQuery({
    queryKey: ["customer-success-tickets", "centers", page, limit, filters],
    queryFn: () => getCustomerSuccessCenters({ page, limit, filters }),
    retry: false,
    placeholderData: (previous) => previous,
  })
